import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Moment } from "moment-timezone";
import { useNavigate } from "react-router-dom";

import useValue from "../../hooks/useValue";
import useTablePagination from "../../hooks/useTablePagination";
import { DefaultPagination } from "../../constants/generalConstants";
import { CustomNotification } from "../../util/CustomNotification";
import * as maintenanceNotificationService from "../../services/maintenanceNotificationService";
import { RouteConstants } from "../../constants/ApplicationConstants/RouteConstants";
import { RootState } from "../../appRedux/store";

import type { NotificationRecord } from "../../shared/interface/Notification/index.interface";

export const useNotification = () => {
  /* CONSTANTS */

  const navigate = useNavigate();

  /* STATE */

  const [notificationList, setNotificationList] = useState<NotificationRecord[]>([]);
  const [reFetch, setRefetch] = useState(false);
  const [dateRange, setDateRange] = useState<{ fromDate: string | null; toDate: string | null }>({
    fromDate: null,
    toDate: null
  });

  const { masterFilters } = useSelector(({ common }: RootState) => common);
  const { value: loading, handleEnableValue: handleEnableLoading, handleDisableValue: handleDisableLoading } = useValue(false);
  const { pagination, handleChangeTotalSize, handleChangePagination, handleResetPagination } = useTablePagination(0);

  /* HANDLER */

  const startIndex = ((pagination?.current ?? 1) - 1) * (pagination?.pageSize ?? 6);

  const handleAddClick = () => {
    navigate(RouteConstants.ADD_NOTIFICATION);
  };

  const handleChangeDatePicker = (dates: [Moment | null, Moment | null] | null) => {
    if (!dates || !dates[0] || !dates[1]) {
      setDateRange({ fromDate: null, toDate: null });
      setRefetch(true);
      return;
    }

    const fromDate = dates[0].format("YYYY-MM-DD");
    const toDate = dates[1].format("YYYY-MM-DD");
    setDateRange({ fromDate, toDate });
  };

  const handleGo = () => {
    handleResetPagination();
    setRefetch(true);
  };

  const generateNotificationSendString = (item: { send_to_web_user?: boolean; send_to_mobile_user?: boolean; send_to_other_user?: boolean }) => {
    const targets = [];
    if (item?.send_to_web_user) targets.push("Web");
    if (item?.send_to_mobile_user) targets.push("Mobile");
    if (item?.send_to_other_user) targets.push("Other");
    return targets.join(", ");
  };

  /* EFFECT */

  useEffect(() => {
    const fetchQueueList = async () => {
      try {
        handleEnableLoading();

        const paramsObject = {
          customerId: masterFilters?.customerId!,
          locationId: masterFilters?.locationId!,
          page: pagination?.current || DefaultPagination.PAGE,
          pageSize: pagination?.pageSize || DefaultPagination.PAGE_SIZE,
          fromDate: dateRange?.fromDate,
          toDate: dateRange?.toDate
        };

        const response = await maintenanceNotificationService.getAllMaintenanceNotification(paramsObject);

        const formattedResponse = response?.data?.rows?.map((item, index) => {
          return {
            ...item,
            sr_no: startIndex + index + 1,
            customerName: item?.customer?.customer_name ?? "",
            locationName: item?.customerLocation?.location_name ?? "",
            notificationSend: generateNotificationSendString(item),
            notificationKey: item?.notificationMaster?.notification_key,
            notification_subject: item?.notificationMaster?.notification_subject
          };
        });

        handleChangeTotalSize(response?.data?.count || 0);
        setNotificationList(formattedResponse);
      } catch (error) {
        CustomNotification.showErrorMessage(error);
      } finally {
        setRefetch(false);
        handleDisableLoading();
      }
    };
    if (reFetch) fetchQueueList();
  }, [reFetch]);

  useEffect(() => {
    if (pagination?.current && pagination?.pageSize) {
      setRefetch(true);
    } else {
      setRefetch(false);
    }
  }, [pagination?.current, pagination?.pageSize, masterFilters?.customerId, masterFilters?.locationId]);

  return { loading, notificationList, pagination, customerId: masterFilters?.customerId, locationId: masterFilters?.locationId, handleGo, handleChangeDatePicker, handleChangePagination, handleAddClick };
};
